'use client'

import {
  useState,
  useEffect,
  useCallback,
  useRef,
  DependencyList,
  Dispatch,
  SetStateAction,
} from 'react'

interface UseAsyncDataOptions<T> {
  /** データ取得成功時のコールバック */
  onSuccess?: (data: T) => void
  /** エラー発生時のコールバック */
  onError?: (error: Error) => void
  /** データ取得を有効にするかどうか（デフォルト: true） */
  enabled?: boolean
}

/** エラー情報の型 */
export interface AsyncDataError {
  message: string
  status?: number
  statusText?: string
}

interface UseAsyncDataResult<T> {
  /** 取得したデータ */
  data: T | null
  /** ローディング状態 */
  isLoading: boolean
  /** エラー情報（status, messageを含む） */
  error: AsyncDataError | null
  /** データを直接更新する関数 */
  setData: Dispatch<SetStateAction<T | null>>
  /** データを再取得する関数 */
  refetch: () => Promise<void>
}

/**
 * 非同期データ取得のための共通フック
 *
 * マウント状態を管理し、アンマウント後のstate更新を防止します。
 *
 * @example
 * ```typescript
 * const { data, isLoading, error } = useAsyncData(
 *   () => ContentService.getStoryById(id),
 *   [id]
 * )
 * ```
 *
 * @example コールバック付き
 * ```typescript
 * const { data, isLoading, error } = useAsyncData(
 *   () => ContentService.getStoryById(id),
 *   [id],
 *   {
 *     onSuccess: (story) => dispatch(setStory(story)),
 *     onError: (error) => console.error('Failed:', error)
 *   }
 * )
 * ```
 */
export function useAsyncData<T>(
  fetchFn: () => Promise<T>,
  deps: DependencyList = [],
  options: UseAsyncDataOptions<T> = {},
): UseAsyncDataResult<T> {
  const { onSuccess, onError, enabled = true } = options

  const [data, setData] = useState<T | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<AsyncDataError | null>(null)

  // マウント状態をrefで管理（refetch時にも参照可能にするため）
  const isMountedRef = useRef(true)

  // fetchFnをrefで保持（依存関係の問題を回避）
  const fetchFnRef = useRef(fetchFn)
  fetchFnRef.current = fetchFn

  // コールバックをrefで保持
  const onSuccessRef = useRef(onSuccess)
  onSuccessRef.current = onSuccess
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError

  const fetchData = useCallback(async () => {
    if (!enabled) {
      setIsLoading(false)
      return
    }

    // リクエストがキャンセルされた場合はfinallyでisLoadingを更新しないためのフラグ
    let wasCancelled = false

    try {
      setIsLoading(true)
      setError(null)

      const result = await fetchFnRef.current()

      if (isMountedRef.current) {
        setData(result)
        onSuccessRef.current?.(result)
      }
    } catch (err) {
      if (isMountedRef.current) {
        const errorMessage = err instanceof Error ? err.message : 'Loading failed'

        // 「Request cancelled」エラーは無視（Strict Modeの二重マウントで発生する想定内の挙動）
        if (errorMessage === 'Request cancelled') {
          wasCancelled = true
          return
        }

        console.error('Loading error:', errorMessage)

        // ApiErrorの場合はstatus情報を含める
        const errorObj = err as { status?: number; statusText?: string }
        setError({
          message: errorMessage,
          status: errorObj.status,
          statusText: errorObj.statusText,
        })

        if (err instanceof Error) {
          onErrorRef.current?.(err)
        } else {
          onErrorRef.current?.(new Error(errorMessage))
        }
      }
    } finally {
      // キャンセルされた場合は別のリクエストが進行中なのでisLoadingを更新しない
      if (isMountedRef.current && !wasCancelled) {
        setIsLoading(false)
      }
    }
  }, [enabled])

  useEffect(() => {
    isMountedRef.current = true
    fetchData()

    return () => {
      isMountedRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, enabled])

  return { data, isLoading, error, setData, refetch: fetchData }
}

export default useAsyncData
